// Вкладка «Мир»: сводки и детали по ресурсам, показателям, угрозам,
// институтам, фракциям, инфраструктуре, преемственности. Вынесено из
// CampaignSetupPage.tsx без изменения поведения (Фаза 1).

import { useState, useEffect, useMemo, type ReactNode } from "react";
import {
  type CampaignContinuity,
  type CampaignThreatOverview,
  type CampaignThreat,
  type CampaignInfrastructureOverview,
  type World,
} from "../api";
import {
  ContinuityWorldSection,
  InfrastructureWorldSection,
  ThreatWorldSection,
  continuityTypeHint,
  continuityTypeOrder,
  continuityTypePlural,
} from "./WorldDomainSections";
import { IconBadge, ParagraphText, ProgressBar, type WorldSection } from "./shared";
import { WorldAssetIcon, WorldSectionAssetHeader, type WorldAssetKind } from "./worldAssets";

type WorldDetailModalState = {
  eyebrow: string;
  title: string;
  body: ReactNode;
};
type WorldDetailOpen = (detail: WorldDetailModalState) => void;
type InfrastructureProjectSource = {
  item: CampaignInfrastructureOverview["projects"]["active"][number];
  bucket: "active" | "completed" | "stalled";
};
function WorldTab({
  world,
  continuity,
  threats,
  infrastructure,
  section,
}: {
  world: World;
  continuity: CampaignContinuity;
  threats: CampaignThreatOverview;
  infrastructure: CampaignInfrastructureOverview;
  section: WorldSection;
}) {
  const [detail, setDetail] = useState<WorldDetailModalState | null>(null);
  const visibleInfrastructure = useMemo(
    () => filterInfrastructureForWorldSummary(infrastructure, continuity),
    [infrastructure, continuity],
  );
  const visibleContinuity = useMemo(
    () => filterContinuityForWorldSummary(continuity, visibleInfrastructure),
    [continuity, visibleInfrastructure],
  );

  useEffect(() => setDetail(null), [section, world.id]);

  const openDetail: WorldDetailOpen = (nextDetail) => setDetail(nextDetail);

  const content =
    section === "resources" ? (
      <WorldResourcesBrief world={world} onOpenDetail={openDetail} />
    ) : section === "indicators" ? (
      <WorldIndicatorsBrief world={world} onOpenDetail={openDetail} />
    ) : section === "institutions" ? (
      <WorldInstitutionsBrief world={world} onOpenDetail={openDetail} />
    ) : section === "factions" ? (
      <WorldFactionsBrief world={world} onOpenDetail={openDetail} />
    ) : section === "threats" ? (
      <WorldThreatsBrief threats={threats} onOpenDetail={openDetail} />
    ) : section === "infrastructure" ? (
      <WorldInfrastructureBrief
        infrastructure={visibleInfrastructure}
        onOpenDetail={openDetail}
      />
    ) : section === "continuity" ? (
      <WorldContinuityBrief
        continuity={visibleContinuity}
        onOpenDetail={openDetail}
      />
    ) : (
      <WorldOverviewBrief
        world={world}
        continuity={visibleContinuity}
        threats={threats}
        infrastructure={visibleInfrastructure}
        onOpenDetail={openDetail}
      />
    );

  return (
    <div className="cc-tab-stack cc-world-brief">
      {content}
      {detail && (
        <WorldDetailModal detail={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
}
function WorldOverviewBrief({
  world,
  continuity,
  threats,
  infrastructure,
  onOpenDetail,
}: {
  world: World;
  continuity: CampaignContinuity;
  threats: CampaignThreatOverview;
  infrastructure: CampaignInfrastructureOverview;
  onOpenDetail: WorldDetailOpen;
}) {
  const goalProgress = estimateWorldGoalProgress(world, threats);
  const sortedThreats = [...threats.tracks].sort(
    (left, right) => right.currentValue - left.currentValue,
  );
  const topThreats = sortedThreats.slice(0, 4);
  const topResources = world.resources.slice(0, 4);
  const topIndicators = world.indicators.slice(0, 4);
  const activeProjects = countInfrastructureProjects(infrastructure, "active");
  const activeAgreements = continuity.active.filter(
    (item) => item.type === "AGREEMENT",
  ).length;
  const openWorldDetails = () =>
    onOpenDetail({
      eyebrow: "Паспорт мира",
      title: world.worldName,
      body: <WorldOverviewDetails world={world} />,
    });

  return (
    <>
      <section className="cc-block cc-world-dashboard-hero">
        <div className="cc-world-dashboard-title">
          <WorldSectionAssetHeader eyebrow="Краткая сводка" title={world.worldName} kind="goal" />
          <button type="button" onClick={openWorldDetails}>Подробнее</button>
        </div>
        <div className="cc-world-goal-card">
          <span>Главная цель</span>
          <h3>{threats.defeat ? threats.defeat.title : "Развить город и не допустить поражения"}</h3>
          <ProgressBar value={goalProgress} />
          <b>{goalProgress}%</b>
          <p>{previewWorldText(threats.defeat?.summary ?? world.summary, 170)}</p>
        </div>
        <div className="cc-world-dashboard-stats">
          <span><b>{formatWorldNumber(infrastructure.summary.capacityScore)}</b> запас</span>
          <span><b>{activeProjects}</b> проектов</span>
          <span><b>{activeAgreements}</b> соглаш.</span>
          <span><b>{threats.tracks.length}</b> угроз</span>
        </div>
      </section>

      <section className="cc-block cc-world-summary-panel">
        <WorldSummaryHeader
          eyebrow="Ресурсы"
          title="Что есть у города"
          action="Все ресурсы"
          onAction={() =>
            onOpenDetail({
              eyebrow: "Ресурсы",
              title: "Запасы и возможности",
              body: <WorldResourcesDetails world={world} />,
            })
          }
        />
        {topResources.length === 0 ? (
          <p className="cc-empty-inline">Ресурсы ещё не сформированы.</p>
        ) : (
          <div className="cc-world-brief-list">
            {topResources.map((resource) => (
              <WorldResourceRow resource={resource} key={resource.code} />
            ))}
          </div>
        )}
      </section>

      <section className="cc-block cc-world-summary-panel">
        <WorldSummaryHeader
          eyebrow="Угрозы"
          title="Что давит на кампанию"
          action="Все угрозы"
          onAction={() =>
            onOpenDetail({
              eyebrow: "Угрозы",
              title: "Треки поражения",
              body: <ThreatWorldSection threats={threats} />,
            })
          }
        />
        {topThreats.length === 0 ? (
          <p className="cc-empty-inline">Угрозы ещё не сформированы.</p>
        ) : (
          <div className="cc-world-brief-list threats">
            {topThreats.map((threat) => (
              <WorldThreatRow threat={threat} key={threat.id} />
            ))}
          </div>
        )}
      </section>

      <section className="cc-block cc-world-summary-panel">
        <WorldSummaryHeader
          eyebrow="Показатели"
          title="Состояние поселения"
          action="Детали"
          onAction={() =>
            onOpenDetail({
              eyebrow: "Показатели",
              title: "Что меняется со временем",
              body: <WorldIndicatorsDetails world={world} />,
            })
          }
        />
        {topIndicators.length === 0 ? (
          <p className="cc-empty-inline">Показатели ещё не сформированы.</p>
        ) : (
          <div className="cc-world-brief-list">
            {topIndicators.map((indicator) => (
              <WorldGaugeRow
                key={indicator.code}
                code={indicator.code}
                label={indicator.displayName}
                value={indicator.currentValue}
                max={100}
                hint={indicatorStateLabel(indicator.currentValue)}
              />
            ))}
          </div>
        )}
      </section>

      <section className="cc-block cc-world-summary-panel">
        <WorldSummaryHeader
          eyebrow="Системы"
          title="Инфраструктура и долгие линии"
          action="Детали"
          onAction={() =>
            onOpenDetail({
              eyebrow: "Системы города",
              title: infrastructure.summary.label,
              body: (
                <WorldInfrastructureAndContinuityDetails
                  infrastructure={infrastructure}
                  continuity={continuity}
                />
              ),
            })
          }
        />
        <div className="cc-world-tile-grid">
          <WorldSummaryTile
            label="Запас прочности"
            value={formatWorldNumber(infrastructure.summary.capacityScore)}
            hint={previewWorldText(infrastructure.summary.summary, 90)}
            assetKind="system"
          />
          <WorldSummaryTile
            label="Нагрузка"
            value={`${infrastructure.summary.maintenanceLoad}/10`}
            hint="Обслуживание систем"
            assetKind="system"
            assetCode="load"
          />
          <WorldSummaryTile
            label="Узкие места"
            value={String(infrastructure.summary.bottleneckCount)}
            hint="Требуют решений"
            assetKind="threat"
            assetCode="bottleneck"
          />
          <WorldSummaryTile
            label="Линии"
            value={String(continuity.active.length)}
            hint="Действуют между циклами"
            assetKind="system"
            assetCode="continuity"
          />
        </div>
      </section>
    </>
  );
}
function WorldResourcesBrief({
  world,
  onOpenDetail,
}: {
  world: World;
  onOpenDetail: WorldDetailOpen;
}) {
  return (
    <section className="cc-block cc-world-summary-panel">
      <WorldSummaryHeader
        eyebrow="Ресурсы"
        title="Краткая сводка запасов"
        action="Подробнее"
        onAction={() =>
          onOpenDetail({
            eyebrow: "Ресурсы",
            title: "Запасы и возможности",
            body: <WorldResourcesDetails world={world} />,
          })
        }
      />
      <div className="cc-world-brief-list">
        {world.resources.map((resource) => (
          <WorldResourceRow resource={resource} key={resource.code} />
        ))}
      </div>
    </section>
  );
}
function WorldIndicatorsBrief({
  world,
  onOpenDetail,
}: {
  world: World;
  onOpenDetail: WorldDetailOpen;
}) {
  return (
    <section className="cc-block cc-world-summary-panel">
      <WorldSummaryHeader
        eyebrow="Показатели"
        title="Состояние общества"
        action="Подробнее"
        onAction={() =>
          onOpenDetail({
            eyebrow: "Показатели",
            title: "Что меняется со временем",
            body: <WorldIndicatorsDetails world={world} />,
          })
        }
      />
      <div className="cc-world-brief-list">
        {world.indicators.map((indicator) => (
          <WorldGaugeRow
            key={indicator.code}
            code={indicator.code}
            label={indicator.displayName}
            value={indicator.currentValue}
            max={100}
            hint={indicatorStateLabel(indicator.currentValue)}
          />
        ))}
      </div>
    </section>
  );
}
function WorldThreatsBrief({
  threats,
  onOpenDetail,
}: {
  threats: CampaignThreatOverview;
  onOpenDetail: WorldDetailOpen;
}) {
  const sortedThreats = [...threats.tracks].sort(
    (left, right) => right.currentValue - left.currentValue,
  );
  return (
    <section className="cc-block cc-world-summary-panel">
      <WorldSummaryHeader
        eyebrow="Угрозы"
        title="Риск коллективного поражения"
        action="Подробнее"
        onAction={() =>
          onOpenDetail({
            eyebrow: "Угрозы",
            title: "Треки поражения",
            body: <ThreatWorldSection threats={threats} />,
          })
        }
      />
      {threats.defeat && (
        <div className="cc-world-alert-card">
          <strong>{threats.defeat.title}</strong>
          <p>{previewWorldText(threats.defeat.summary, 160)}</p>
        </div>
      )}
      {sortedThreats.length === 0 ? (
        <p className="cc-empty-inline">Угрозы ещё не сформированы.</p>
      ) : (
        <div className="cc-world-brief-list threats">
          {sortedThreats.map((threat) => (
            <WorldThreatRow threat={threat} key={threat.id} />
          ))}
        </div>
      )}
    </section>
  );
}
function WorldInstitutionsBrief({
  world,
  onOpenDetail,
}: {
  world: World;
  onOpenDetail: WorldDetailOpen;
}) {
  return (
    <section className="cc-block cc-world-summary-panel">
      <WorldSummaryHeader
        eyebrow="Институты"
        title="Официальная система"
        action="Подробнее"
        onAction={() =>
          onOpenDetail({
            eyebrow: "Институты",
            title: "Официальная система",
            body: <WorldInstitutionsDetails world={world} />,
          })
        }
      />
      <div className="cc-world-brief-list">
        {world.institutions.map((institution) => (
          <WorldDualGaugeRow
            key={institution.code}
            kind="institution"
            code={institution.code}
            label={institution.displayName}
            primaryLabel="Уровень"
            primary={institution.level}
            secondaryLabel="Нагрузка"
            secondary={institution.load}
            hint={institutionStateLabel(institution.level, institution.load)}
          />
        ))}
      </div>
    </section>
  );
}
function WorldFactionsBrief({
  world,
  onOpenDetail,
}: {
  world: World;
  onOpenDetail: WorldDetailOpen;
}) {
  return (
    <section className="cc-block cc-world-summary-panel">
      <WorldSummaryHeader
        eyebrow="Фракции"
        title="Самостоятельные силы"
        action="Подробнее"
        onAction={() =>
          onOpenDetail({
            eyebrow: "Фракции",
            title: "Самостоятельные силы",
            body: <WorldFactionsDetails world={world} />,
          })
        }
      />
      <div className="cc-world-brief-list">
        {world.factions.map((faction) => (
          <WorldDualGaugeRow
            key={faction.code}
            kind="faction"
            code={faction.code}
            label={faction.displayName}
            primaryLabel="Вес"
            primary={faction.influence}
            secondaryLabel="Отношение"
            secondary={faction.attitude}
            hint={factionStateLabel(faction.influence, faction.attitude)}
          />
        ))}
      </div>
    </section>
  );
}
function WorldInfrastructureBrief({
  infrastructure,
  onOpenDetail,
}: {
  infrastructure: CampaignInfrastructureOverview;
  onOpenDetail: WorldDetailOpen;
}) {
  return (
    <section className="cc-block cc-world-summary-panel">
      <WorldSummaryHeader
        eyebrow="Инфраструктура"
        title={infrastructure.summary.label}
        action="Подробнее"
        onAction={() =>
          onOpenDetail({
            eyebrow: "Инфраструктура",
            title: infrastructure.summary.label,
            body: <InfrastructureWorldSection infrastructure={infrastructure} />,
          })
        }
      />
      <p className="cc-world-summary-copy">
        {previewWorldText(infrastructure.summary.summary, 180)}
      </p>
      <div className="cc-world-tile-grid">
        <WorldSummaryTile label="Запас" value={formatWorldNumber(infrastructure.summary.capacityScore)} hint="Прочность систем" />
        <WorldSummaryTile label="Нагрузка" value={`${infrastructure.summary.maintenanceLoad}/10`} hint="Обслуживание" />
        <WorldSummaryTile label="Проекты" value={String(countInfrastructureProjects(infrastructure, "active"))} hint="В работе" />
        <WorldSummaryTile label="Узкие места" value={String(infrastructure.summary.bottleneckCount)} hint="Риски" />
      </div>
    </section>
  );
}
function WorldContinuityBrief({
  continuity,
  onOpenDetail,
}: {
  continuity: CampaignContinuity;
  onOpenDetail: WorldDetailOpen;
}) {
  const activeByType = continuityTypeOrder
    .map((type) => ({ type, count: continuity.active.filter((item) => item.type === type).length }))
    .filter((item) => item.count > 0);
  return (
    <section className="cc-block cc-world-summary-panel">
      <WorldSummaryHeader
        eyebrow="Наследие решений"
        title="Долгие линии"
        action="Подробнее"
        onAction={() =>
          onOpenDetail({
            eyebrow: "Наследие решений",
            title: "Проекты и договорённости",
            body: <ContinuityWorldSection continuity={continuity} />,
          })
        }
      />
      {activeByType.length === 0 ? (
        <p className="cc-empty-inline">Долгосрочных линий пока нет.</p>
      ) : (
        <div className="cc-world-tile-grid">
          {activeByType.map((item) => (
            <WorldSummaryTile
              key={item.type}
              label={continuityTypePlural(item.type)}
              value={String(item.count)}
              hint={continuityTypeHint(item.type)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
function WorldSummaryHeader({
  eyebrow,
  title,
  action,
  onAction,
}: {
  eyebrow: string;
  title: string;
  action: string;
  onAction: () => void;
}) {
  return (
    <header className="cc-world-summary-header">
      <div>
        <span>{eyebrow}</span>
        <h3>{title}</h3>
      </div>
      <button type="button" onClick={onAction}>{action}</button>
    </header>
  );
}
function WorldSummaryTile({
  label,
  value,
  hint,
  assetKind,
  assetCode,
}: {
  label: string;
  value: string;
  hint: string;
  assetKind?: WorldAssetKind;
  assetCode?: string;
}) {
  return (
    <div className={`cc-world-summary-tile${assetKind ? " with-asset" : ""}`}>
      {assetKind && <WorldAssetIcon kind={assetKind} code={assetCode} label={label} />}
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{hint}</small>
    </div>
  );
}
function WorldResourceRow({
  resource,
}: {
  resource: World["resources"][number];
}) {
  const percent = resource.maxValue > 0 ? (resource.currentValue / resource.maxValue) * 100 : 0;
  return (
    <div className="cc-world-brief-row resource">
      <WorldAssetIcon kind="resource" code={resource.code} label={resource.displayName} />
      <div>
        <strong>{resource.displayName}</strong>
        <ProgressBar value={percent} />
      </div>
      <b>{resource.currentValue}/{resource.maxValue}</b>
    </div>
  );
}
function WorldThreatRow({ threat }: { threat: CampaignThreat }) {
  return (
    <div className={`cc-world-brief-row threat ${threat.level.toLowerCase()}`}>
      <WorldAssetIcon kind="threat" code={threat.title} label={threat.title} />
      <div>
        <strong>{threat.title}</strong>
        <ProgressBar value={threat.currentValue} />
      </div>
      <b>{threat.currentValue}</b>
    </div>
  );
}
function WorldGaugeRow({
  label,
  value,
  max,
  hint,
  code,
}: {
  label: string;
  value: number;
  max: number;
  hint: string;
  code?: string;
}) {
  return (
    <div className="cc-world-brief-row">
      <WorldAssetIcon kind="indicator" code={code} label={label} />
      <div>
        <strong>{label}</strong>
        <ProgressBar value={(value / max) * 100} />
      </div>
      <b>{value}</b>
      <small>{hint}</small>
    </div>
  );
}
function WorldDualGaugeRow({
  label,
  primaryLabel,
  primary,
  secondaryLabel,
  secondary,
  hint,
  kind,
  code,
}: {
  label: string;
  primaryLabel: string;
  primary: number;
  secondaryLabel: string;
  secondary: number;
  hint: string;
  kind: "institution" | "faction";
  code?: string;
}) {
  return (
    <div className="cc-world-brief-row dual">
      <WorldAssetIcon kind={kind} code={code} label={label} />
      <div>
        <strong>{label}</strong>
        <small>{hint}</small>
      </div>
      <b>{primaryLabel} {primary}</b>
      <em>{secondaryLabel} {secondary}</em>
    </div>
  );
}
function WorldDetailModal({
  detail,
  onClose,
}: {
  detail: WorldDetailModalState;
  onClose: () => void;
}) {
  return (
    <div className="cc-modal-backdrop cc-world-detail-modal-backdrop" role="dialog" aria-modal="true">
      <div className="cc-modal-card cc-world-detail-modal">
        <header className="cc-modal-header cc-world-detail-modal-header">
          <div>
            <span>{detail.eyebrow}</span>
            <h2>{detail.title}</h2>
          </div>
          <button type="button" className="cc-modal-close" onClick={onClose}>×</button>
        </header>
        <div className="cc-world-detail-body">{detail.body}</div>
      </div>
    </div>
  );
}
function WorldOverviewDetails({ world }: { world: World }) {
  return (
    <div className="cc-world-detail-stack">
      <ParagraphText text={world.summary} />
      <dl className="cc-world-passport cc-world-detail-passport">
        <div><dt>Устройство</dt><dd>{world.governanceName}</dd></div>
        <div><dt>Технологии</dt><dd>{world.technologyLevel}</dd></div>
        <div><dt>Стиль</dt><dd>{world.visualStyle}</dd></div>
      </dl>
      {world.contextThemes.length > 0 && (
        <div className="cc-world-detail-section">
          <strong>Ключевые условия</strong>
          <div className="theme-grid">
            {world.contextThemes.map((theme) => (
              <article className="theme-card" key={theme.code}>
                <IconBadge icon="sparkles" />
                <div>
                  <strong>{theme.title}</strong>
                  <p>{theme.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function WorldResourcesDetails({ world }: { world: World }) {
  return (
    <div className="cc-world-detail-stack">
      <MechanicHint
        title="Как влияют на игру"
        text="Ресурсы оплачивают инициативы и отражают реальные возможности города. Их состав и стартовое состояние формируются из описания мира."
      />
      <div className="metric-grid">
        {world.resources.map((resource) => (
          <article className="metric-card" key={resource.code}>
            <div className="cc-metric-head">
              <WorldAssetIcon kind="resource" code={resource.code} label={resource.displayName} />
              <div>
                <small className="metric-kind">
                  {resourceStateLabel(resource.currentValue, resource.maxValue)}
                </small>
                <span>{resource.displayName}</span>
              </div>
            </div>
            <strong>{resource.currentValue}<small>/{resource.maxValue}</small></strong>
            <ProgressBar value={(resource.currentValue / resource.maxValue) * 100} />
            <p>{resource.description}</p>
          </article>
        ))}
      </div>
    </div>
  );
}
function WorldIndicatorsDetails({ world }: { world: World }) {
  return (
    <div className="cc-world-detail-stack">
      <MechanicHint
        title="Как влияют на игру"
        text="Показатели отражают состояние общества. Они не расходуются напрямую: решения, кризисы и поддержка фракций повышают или снижают их."
      />
      <div className="indicator-list">
        {world.indicators.map((indicator) => (
          <article className="indicator-row" key={indicator.code}>
            <WorldAssetIcon kind="indicator" code={indicator.code} label={indicator.displayName} />
            <div>
              <strong>{indicator.displayName}</strong>
              <span>{indicator.currentValue}</span>
            </div>
            <div>
              <ProgressBar value={indicator.currentValue} />
              <p>{indicator.description}</p>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
function WorldInstitutionsDetails({ world }: { world: World }) {
  return (
    <div className="cc-world-detail-stack">
      <MechanicHint
        title="Как влияют"
        text="Состояние учреждения показывает, насколько уверенно оно справляется с обязанностями и текущим давлением."
      />
      <div className="cc-card-list">
        {world.institutions.map((institution) => (
          <article className="cc-mechanic-card" key={institution.code}>
            <div className="cc-mechanic-title">
              <WorldAssetIcon kind="institution" code={institution.code} label={institution.displayName} />
              <div>
                <h3>{institution.displayName}</h3>
                <span>{institutionStateLabel(institution.level, institution.load)}</span>
              </div>
            </div>
            <p>{institution.description}</p>
          </article>
        ))}
      </div>
    </div>
  );
}
function WorldFactionsDetails({ world }: { world: World }) {
  return (
    <div className="cc-world-detail-stack">
      <MechanicHint
        title="Как влияют"
        text="Вес фракции и её отношение к Совету определяют готовность помогать, сопротивляться или выдвигать собственные условия."
      />
      <div className="cc-card-list">
        {world.factions.map((faction) => (
          <article className="cc-mechanic-card" key={faction.code}>
            <div className="cc-mechanic-title">
              <WorldAssetIcon kind="faction" code={faction.code} label={faction.displayName} />
              <div>
                <h3>{faction.displayName}</h3>
                <span>{factionStateLabel(faction.influence, faction.attitude)}</span>
              </div>
            </div>
            <p>{faction.description}</p>
            <dl>
              <div><dt>Хотят</dt><dd>{faction.demands}</dd></div>
              <div><dt>Могут дать</dt><dd>{faction.support}</dd></div>
            </dl>
          </article>
        ))}
      </div>
    </div>
  );
}
function WorldInfrastructureAndContinuityDetails({
  infrastructure,
  continuity,
}: {
  infrastructure: CampaignInfrastructureOverview;
  continuity: CampaignContinuity;
}) {
  return (
    <div className="cc-world-detail-stack">
      <InfrastructureWorldSection infrastructure={infrastructure} />
      <ContinuityWorldSection continuity={continuity} />
    </div>
  );
}
function filterInfrastructureForWorldSummary(
  infrastructure: CampaignInfrastructureOverview,
  continuity: CampaignContinuity,
): CampaignInfrastructureOverview {
  const agreementTitles = new Set(
    continuity.active
      .filter((item) => item.type === "AGREEMENT")
      .map((item) => normalizeWorldAffairKey(item.title)),
  );
  const filterProject = (item: CampaignInfrastructureOverview["projects"]["active"][number]) =>
    !agreementTitles.has(normalizeWorldAffairKey(item.title));
  return {
    ...infrastructure,
    projects: {
      ...infrastructure.projects,
      active: infrastructure.projects.active.filter(filterProject),
      completed: infrastructure.projects.completed.filter(filterProject),
      stalled: infrastructure.projects.stalled.filter(filterProject),
    },
    summary: {
      ...infrastructure.summary,
      activeProjectCount: infrastructure.projects.active.filter(filterProject).length,
    },
  };
}
function filterContinuityForWorldSummary(
  continuity: CampaignContinuity,
  infrastructure: CampaignInfrastructureOverview,
): CampaignContinuity {
  const projectSources = infrastructureProjectSources(infrastructure);
  const projectIds = new Set(projectSources.map(({ item }) => item.id));
  const projectTitles = new Set(
    projectSources.map(({ item }) => normalizeWorldAffairKey(item.title)),
  );
  const active = continuity.active.filter((item) => {
    if (item.type !== "PROJECT") return true;
    const titleKey = normalizeWorldAffairKey(item.title);
    return !projectIds.has(item.id) && !projectIds.has(item.sourceId) && !projectTitles.has(titleKey);
  });
  return { ...continuity, active };
}
function infrastructureProjectSources(
  infrastructure: CampaignInfrastructureOverview,
): InfrastructureProjectSource[] {
  return [
    ...infrastructure.projects.active.map((item) => ({ item, bucket: "active" as const })),
    ...infrastructure.projects.completed.map((item) => ({ item, bucket: "completed" as const })),
    ...infrastructure.projects.stalled.map((item) => ({ item, bucket: "stalled" as const })),
  ];
}
function countInfrastructureProjects(
  infrastructure: CampaignInfrastructureOverview,
  bucket: "active" | "completed" | "stalled",
) {
  return infrastructure.projects[bucket].length;
}
function estimateWorldGoalProgress(world: World, threats: CampaignThreatOverview) {
  if (threats.defeat) return 0;
  const resourceScore = averageWorldValue(
    world.resources.map((resource) =>
      resource.maxValue > 0 ? (resource.currentValue / resource.maxValue) * 100 : 0,
    ),
  );
  const indicatorScore = averageWorldValue(world.indicators.map((item) => item.currentValue));
  const threatPenalty = averageWorldValue(threats.tracks.map((item) => item.currentValue));
  return Math.round(Math.max(0, Math.min(100, (resourceScore + indicatorScore) / 2 - threatPenalty * 0.35)));
}
function averageWorldValue(values: number[]) {
  if (values.length === 0) return 50;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}
function formatWorldNumber(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
function indicatorStateLabel(value: number) {
  if (value >= 75) return "Сильная сторона";
  if (value >= 50) return "Устойчиво";
  if (value >= 30) return "Напряжение";
  return "Критично";
}
function previewWorldText(text: string | undefined, maxLength: number) {
  const normalized = (text ?? "").replace(/\s+/g, " ").trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}
function normalizeWorldAffairKey(value: string | undefined) {
  return (value ?? "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();
}
function MechanicHint({ title, text }: { title: string; text: string }) {
  return (
    <div className="cc-mechanic-hint">
      <strong>{title}</strong>
      <p>{text}</p>
    </div>
  );
}
function resourceStateLabel(value: number, max: number) {
  const ratio = max > 0 ? value / max : 0;
  return ratio <= 0.2
    ? "Почти исчерпан"
    : ratio <= 0.4
      ? "Острый дефицит"
      : ratio <= 0.6
        ? "Ограниченный запас"
        : ratio <= 0.8
          ? "Устойчивый запас"
          : "Хорошо обеспечен";
}
function institutionStateLabel(level: number, load: number) {
  const capacity =
    level <= 1
      ? "едва действует"
      : level === 2
        ? "работает неуверенно"
        : level === 3
          ? "сохраняет работоспособность"
          : "действует уверенно";
  const pressure =
    load >= 8
      ? "предельно перегружен"
      : load >= 5
        ? "испытывает сильное давление"
        : load >= 3
          ? "заметно загружен"
          : "справляется с нагрузкой";
  return `${capacity}, ${pressure}`;
}
function factionStateLabel(influence: number, attitude: number) {
  const weight =
    influence >= 4
      ? "одна из ведущих сил"
      : influence === 3
        ? "заметная сила"
        : influence === 2
          ? "имеет ограниченный вес"
          : "пока слабо влияет";
  const relation =
    attitude >= 4
      ? "поддерживает Совет"
      : attitude >= 1
        ? "скорее расположена к Совету"
        : attitude <= -4
          ? "открыто враждебна Совету"
          : attitude <= -1
            ? "относится к Совету настороженно"
            : "сохраняет нейтралитет";
  return `${weight}, ${relation}`;
}

export {
  WorldTab,
  WorldOverviewBrief,
  WorldResourcesBrief,
  WorldIndicatorsBrief,
  WorldThreatsBrief,
  WorldInstitutionsBrief,
  WorldFactionsBrief,
  WorldInfrastructureBrief,
  WorldContinuityBrief,
  WorldSummaryHeader,
  WorldSummaryTile,
  WorldResourceRow,
  WorldThreatRow,
  WorldGaugeRow,
  WorldDualGaugeRow,
  WorldDetailModal,
  WorldOverviewDetails,
  WorldResourcesDetails,
  WorldIndicatorsDetails,
  WorldInstitutionsDetails,
  WorldFactionsDetails,
  WorldInfrastructureAndContinuityDetails,
  filterInfrastructureForWorldSummary,
  filterContinuityForWorldSummary,
  infrastructureProjectSources,
  countInfrastructureProjects,
  estimateWorldGoalProgress,
  averageWorldValue,
  formatWorldNumber,
  indicatorStateLabel,
  previewWorldText,
  normalizeWorldAffairKey,
  resourceStateLabel,
  institutionStateLabel,
  factionStateLabel,
  MechanicHint,
};
export type { InfrastructureProjectSource, WorldDetailModalState, WorldDetailOpen };
